/**
 * Servicio para gestión de configuración de usuario
 */

import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../../lib/firebase';
import type { UserSettings } from '../types';
import { LoggerService } from './logger.service';

export class UserSettingsService {
  private static readonly collectionName = 'userSettings';

  /**
   * Obtener configuración del usuario
   */
  static async getUserSettings(userId: string): Promise<UserSettings | null> {
    try {
      LoggerService.info('Obteniendo configuración de usuario', 'UserSettingsService', { userId });

      const docRef = doc(db, this.collectionName, userId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          userId: data.userId,
          alertas: data.alertas,
          createdAt: data.createdAt instanceof Timestamp ? data.createdAt.toDate() : new Date(data.createdAt),
          updatedAt: data.updatedAt instanceof Timestamp ? data.updatedAt.toDate() : new Date(data.updatedAt),
        };
      }

      return null;
    } catch (error) {
      LoggerService.error('Error al obtener configuración de usuario', 'UserSettingsService', {
        userId,
        error: error instanceof Error ? error.message : 'Error desconocido',
      });
      throw error;
    }
  }

  /**
   * Crear configuración inicial del usuario con valores por defecto
   */
  static async createDefaultSettings(userId: string): Promise<UserSettings> {
    try {
      LoggerService.info('Creando configuración por defecto', 'UserSettingsService', { userId });

      const now = new Date();
      const defaultSettings: Omit<UserSettings, 'id'> = {
        userId,
        alertas: {
          umbralAumentoGasto: 20, // 20% de aumento
          umbralDisminucionGasto: 20, // 20% de disminución
          categoriasAMonitorear: [], // Vacío = monitorear todas
        },
        createdAt: now,
        updatedAt: now,
      };

      const docRef = doc(db, this.collectionName, userId);
      await setDoc(docRef, {
        ...defaultSettings,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      });

      LoggerService.logUserAction('user_settings_created', 'UserSettingsService', { userId });

      return {
        id: userId,
        ...defaultSettings,
      };
    } catch (error) {
      LoggerService.error('Error al crear configuración', 'UserSettingsService', {
        userId,
        error: error instanceof Error ? error.message : 'Error desconocido',
      });
      throw error;
    }
  }

  /**
   * Actualizar configuración del usuario
   */
  static async updateUserSettings(
    userId: string,
    settings: Partial<Pick<UserSettings, 'alertas'>>
  ): Promise<void> {
    try {
      LoggerService.info('Actualizando configuración de usuario', 'UserSettingsService', { userId, settings });

      const docRef = doc(db, this.collectionName, userId);
      await updateDoc(docRef, {
        ...settings,
        updatedAt: Timestamp.fromDate(new Date()),
      });

      LoggerService.logUserAction('user_settings_updated', 'UserSettingsService', { userId });
    } catch (error) {
      LoggerService.error('Error al actualizar configuración', 'UserSettingsService', {
        userId,
        error: error instanceof Error ? error.message : 'Error desconocido',
      });
      throw error;
    }
  }

  /**
   * Obtener o crear configuración del usuario
   */
  static async getOrCreateUserSettings(userId: string): Promise<UserSettings> {
    const existing = await this.getUserSettings(userId);
    if (existing) {
      return existing;
    }
    return this.createDefaultSettings(userId);
  }
}
